import { ArrowRightIcon } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
  FormDateFieldDS,
  FormTelephoneInputDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { SlabAddressFields } from 'components/reusable/form/SlabAddressFields'
import { Banner, Button, ButtonGroup, useBreakpoint } from 'slab'
import {
  calculateAgeAtDate,
  validatePOBoxAddressLine1,
  validatePOBoxAddressLine2,
} from 'utils'

import { OnboardingClearEnrollmentsModal } from '../components/OnboardingClearEnrollmentsModal'
import { OnboardingGenericErrorMessage } from '../components/OnboardingGenericErrorMessage'
import { useOnboarding } from '../OnboardingContext'
import { type StepComponentProps } from '../types'
import { generateRandomPhoneNumber, hasExistingOnThatchEnrollments } from '../utils'

type BasicInfoProps = {
  given_name: string;
  family_name: string;
  date_of_birth: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
  phone_number: string;
}

export function GenerateAllowancesPage ({
  navigateToNextStep,
}: StepComponentProps) {
  const {
    onboardingData,
    mapboxAccessToken,
    updateMemberInfo,
    isHawaiiState,
    setIsHawaiiState,
    prefillDummyData,
  } = useOnboarding()
  const [submitError, setSubmitError] = useState<ReactElement | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<BasicInfoProps | null>(
    null
  )

  const getDummyData = (field: keyof BasicInfoProps) => {
    if (!prefillDummyData) {
      return ''
    }

    return {
      given_name: 'John',
      family_name: 'Rosen',
      date_of_birth: '1990-01-01',
      address_line1: '455 N Rexford Dr',
      address_line2: '',
      city: 'Beverly Hills',
      state: 'CA',
      zip: '90210',
      phone_number: generateRandomPhoneNumber(),
    }[field]
  }

  const getDefaultValues = () => ({
    given_name:
      onboardingData?.member?.given_name ||
      onboardingData?.employee.first_name ||
      getDummyData('given_name'),
    family_name:
      onboardingData?.member?.family_name ||
      onboardingData?.employee.last_name ||
      getDummyData('family_name'),
    date_of_birth: onboardingData?.member?.date_of_birth || getDummyData('date_of_birth'),
    address_line1: onboardingData?.member?.address_line1 || getDummyData('address_line1'),
    address_line2: onboardingData?.member?.address_line2 || getDummyData('address_line2'),
    city: onboardingData?.member?.city || getDummyData('city'),
    state: onboardingData?.member?.state || getDummyData('state'),
    zip: onboardingData?.member?.zip || getDummyData('zip'),
    phone_number: onboardingData?.member?.phone_number || getDummyData('phone_number'),
  })

  const { breakpointIsAtLeast } = useBreakpoint()

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    trigger,
    setError,
    clearErrors,
    watch,
    formState: { errors, isValid, dirtyFields },
  } = useForm<BasicInfoProps>({
    defaultValues: getDefaultValues(),
    mode: 'onChange',
  })

  // Watch for state changes
  const stateValue = watch('state')

  // Update isHawaiiState when state changes
  useEffect(() => {
    setIsHawaiiState(stateValue === 'HI')
  }, [stateValue, setIsHawaiiState])

  // This hook is needed in case the data refreshes.
  useEffect(() => {
    reset(getDefaultValues())
  }, [onboardingData])

  // Check for phone verification restriction
  useEffect(() => {
    if (
      onboardingData?.restrictions?.phone_number_verification ===
      'can_not_start_phone_verification'
    ) {
      setError('phone_number', {
        type: 'manual',
        message:
          'We are unable to verify your phone number. Please double check your number or contact support for more assistance.',
      })
    }
  }, [onboardingData, setError])

  const maybeSubmit = (formData: BasicInfoProps) => {
    const changedImportantFields =
      dirtyFields.date_of_birth ||
      dirtyFields.address_line1 ||
      dirtyFields.city ||
      dirtyFields.state ||
      dirtyFields.zip
    const existingEnrollments = hasExistingOnThatchEnrollments(onboardingData)
    if (changedImportantFields && existingEnrollments) {
      setPendingFormData(formData)
      setShowModal(true)
    } else {
      onSubmit(formData)
    }
  }

  const onSubmit = (formData: BasicInfoProps) => {
    // when the user enters an address, we automatically set is_state_resident to true
    const dataToSubmit = { member: { ...formData, is_state_resident: true } }
    updateMemberInfo.mutate(dataToSubmit, {
      onSuccess: (updatedData) => {
        if (
          updatedData?.restrictions?.phone_number_verification ===
          'can_not_start_phone_verification'
        ) {
          setError('phone_number', {
            type: 'manual',
            message:
              'We are unable to verify your phone number. Please double check your number or contact support for more assistance.',
          })
        } else {
          navigateToNextStep(updatedData)
        }
      },
      onError: (error) => {
        if (error.status === 422) {
          const fieldErrors = error.fieldErrors!
          Object.entries(fieldErrors).forEach(([field, errorMessages]) => {
            setError(field as keyof BasicInfoProps, {
              type: 'manual',
              message: errorMessages.join(', '),
            })
          })
        } else {
          const errorMessage = <OnboardingGenericErrorMessage error={error} />
          setSubmitError(errorMessage)
        }
      },
    })
  }

  return (
    <div data-testid='GenerateAllowancePage' className='max-w-[44rem]'>
      <div className='mb-10'>
        <h1 className='heading-lg text-gray-800 mb-3'>Tell us about you</h1>
        <p className='body text-gray-500'>
          We need a few pieces of information to get started.
        </p>
      </div>
      <form onSubmit={handleSubmit(maybeSubmit)}>
        <div className='flex flex-col mb-10 gap-y-4 xs:gap-y-6'>
          <div className='flex gap-x-4'>
            <FormTextFieldDS<BasicInfoProps>
              error={errors.given_name}
              disabled={updateMemberInfo.isPending}
              fieldId='given_name'
              fieldLabel='First name'
              register={register}
              placeholder='E.g. Jane'
              validationRule={{ required: 'First name is required.' }}
            />
            <FormTextFieldDS<BasicInfoProps>
              error={errors.family_name}
              disabled={updateMemberInfo.isPending}
              fieldId='family_name'
              fieldLabel='Last name'
              register={register}
              placeholder='E.g. Doe'
              validationRule={{ required: 'Last name is required.' }}
            />
          </div>
          <div className='flex flex-col gap-y-2'>
            <SlabAddressFields<BasicInfoProps>
              accessToken={mapboxAccessToken!}
              errors={errors}
              disabled={updateMemberInfo.isPending}
              getValues={getValues}
              setValue={setValue}
              register={register}
              trigger={trigger}
              setError={setError}
              clearErrors={clearErrors}
              watch={watch}
              validationRules={{
                address_line1: {
                  required: 'Street address is required.',
                  validate: (value: string) =>
                    validatePOBoxAddressLine1(value, false),
                },
                address_line2: {
                  validate: (value: string) =>
                    validatePOBoxAddressLine2(value, false),
                },
                city: {
                  required: 'City is required.',
                },
                state: {
                  required: 'State is required.',
                },
                zip: {
                  required: 'Zip code is required.',
                },
              }}
            />
          </div>
          <div className='flex gap-x-4'>
            <FormDateFieldDS<BasicInfoProps>
              error={errors.date_of_birth}
              disabled={updateMemberInfo.isPending}
              fieldId='date_of_birth'
              fieldLabel='Date of birth'
              register={register}
              placeholder='MM/DD/YYYY'
              validationRule={{
                required: 'Date of birth is required.',
                validate: {
                  validYear: (value: string) => {
                    const age = calculateAgeAtDate(value, new Date())
                    if (age > 120) {
                      return 'Date of birth cannot be more than 120 years ago'
                    } else if (age < 0) {
                      return 'Date of birth cannot be in the future'
                    }
                    return true
                  },
                },
              }}
            />
            <FormTelephoneInputDS<BasicInfoProps>
              error={errors.phone_number}
              disabled={updateMemberInfo.isPending}
              fieldId='phone_number'
              fieldLabel='Phone number'
              register={register}
              placeholder='(123) 456-7890'
              validationRule={{ required: 'Phone number is required.' }}
            />
          </div>
        </div>
        {(submitError ||
          onboardingData?.restrictions?.allowance_generation_failed ||
          onboardingData?.restrictions?.member_missing_county) && (
            <div className='mb-10'>
              <Banner
                title={
                onboardingData?.restrictions?.allowance_generation_failed
                  ? 'We are missing something'
                  : 'A problem has occurred while processing your request'
              }
                type='critical'
              >
                {submitError}
                {onboardingData?.restrictions?.allowance_generation_failed && (
                  <p>
                    There isn't an allowance set up for your particular scenario.
                    If your address and date of birth look right, please reach out
                    to <a href='mailto:support@thatch.com'>support@thatch.com</a>{' '}
                    for help.
                  </p>
                )}
                {onboardingData?.restrictions?.member_missing_county && (
                  <p>
                    We were unable to determine the correct county from the provided address.
                    If your address looks right, please reach out
                    to <a href='mailto:support@thatch.com'>support@thatch.com</a>{' '}
                    for help.
                  </p>
                )}
              </Banner>
            </div>
        )}

        {/* Hawaii state banner */}
        {isHawaiiState && (
          <div className='mb-10'>
            <Banner
              title="Sorry, we can't offer plans in Hawaii"
              type='warning'
            >
              <p>
                We are currently unable to enroll members from Hawaii. Please
                contact our support team at{' '}
                <a href='mailto:support@thatch.com'>support@thatch.com</a> for
                more information.
              </p>
            </Banner>
          </div>
        )}
        <ButtonGroup
          size='large'
          layout={breakpointIsAtLeast('xs') ? 'horizontal' : 'vertical'}
        >
          <Button
            rightIcon={ArrowRightIcon}
            variant='primary'
            type='submit'
            disabled={updateMemberInfo.isPending || isHawaiiState || !isValid}
          >
            Next
          </Button>
        </ButtonGroup>
      </form>
      <OnboardingClearEnrollmentsModal
        isModalOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setPendingFormData(null)
        }}
        onSubmit={() => {
          if (pendingFormData) {
            onSubmit(pendingFormData!)
            setShowModal(false)
          } else {
            // this should never happen
            setError('root', {
              type: 'manual',
              message:
                'Something went wrong submitting this form. Please try again.',
            })
          }
        }}
        description='Your date of birth and address can impact plan cost and availability, so making a change here will remove any plans currently in your cart'
      />
    </div>
  )
}
